import {
  Button,
  ErrorBoundary,
  FlexBox,
  Icon,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import type {
  ContextControl,
  PeriodControl,
  SettingsControl,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';
import { useToggle, useUpdateEffect } from 'react-use';

import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { analyticsActions } from '../../analytics.actions';
import { useAnalyticsState } from '../panels/utils/useAnalyticsState/useAnalyticsState';

import styles from './analyticsControls.module.scss';
import { AnalyticsContext } from './components/context/analyticsContext';
import { AnalyticsFilters } from './components/filters/analyticsFilters';
import { AnalyticsPeriod } from './components/period/analyticsPeriod';

export const AnalyticsControls = (): JSX.Element => {
  const { dashboard, context, period, settings } = useAnalyticsState();
  const dispatch = useAppDispatch();

  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const [hasCollapsedFilters, toggleHasCollapseFilters] = useToggle(
    !!isSmallScreen,
  );

  useUpdateEffect(() => {
    toggleHasCollapseFilters(!!isSmallScreen);
  }, [isSmallScreen]);

  /** Update the selected context in the store. */
  const handleChangeContext = useCallback(
    (context: ContextControl) => {
      dispatch(analyticsActions.setContext({ context }));

      ampli.analyticsContextUpdated({
        dashboard: dashboard.title,
      });
    },
    [dashboard.title, dispatch],
  );

  /** Update the selected period in the store. */
  const handleChangePeriod = (period: PeriodControl) => {
    dispatch(analyticsActions.setPeriod({ period }));

    ampli.analyticsPeriodUpdated({
      dashboard: dashboard.title,
      value:
        period.values.length === 1
          ? period.values[0].value
          : 'dateWithDefaultTime',
      timezone: settings.timezone,
    });
  };

  /** Update the selected settings in the store. */
  const handleChangeSettings = (newSettings: SettingsControl) => {
    dispatch(
      analyticsActions.setSettings({
        settings: { ...settings, ...newSettings },
      }),
    );

    if (newSettings.timezone !== settings.timezone) {
      ampli.analyticsTimezoneUpdated({
        dashboard: dashboard.title,
        timezone: newSettings.timezone,
      });
    }

    if (newSettings.schedule_id !== settings.schedule_id) {
      if (newSettings.schedule_id) {
        ampli.analyticsBusinessHoursScheduleUpdated({
          dashboard: dashboard.title,
          timezone: newSettings.timezone,
        });
      } else {
        ampli.analyticsBusinessHoursScheduleRemoved({
          dashboard: dashboard.title,
          timezone: settings.timezone,
        });
      }
    }
  };

  return (
    <ErrorBoundary
      fallback={
        <Text.Paragraph color={colors.red[500]}>
          Filters are currently unavailable due to an unexpected error. Our team
          has been notified and is looking into the issue.{' '}
          <a href="#" onClick={() => window.location.reload()}>
            Try reloading.
          </a>
        </Text.Paragraph>
      }
    >
      <div className={styles.controls}>
        <FlexBox gap="300" alignItems="center">
          {dashboard.context && (
            <AnalyticsContext
              icon={dashboard.icon}
              context={dashboard.context}
              control={context}
              onChange={handleChangeContext}
            />
          )}

          <AnalyticsPeriod
            period={period}
            settings={settings}
            onChangePeriod={handleChangePeriod}
            onChangeSettings={handleChangeSettings}
          />
        </FlexBox>

        {isSmallScreen && (
          <Button
            variant="tertiary"
            slotStart={<Icon name={hasCollapsedFilters ? 'plus' : 'minus'} />}
            onClick={toggleHasCollapseFilters}
          >
            {hasCollapsedFilters ? 'More filters' : 'Less filters'}
          </Button>
        )}

        {dashboard.filters.length > 0 && (
          <AnalyticsFilters
            dashboardFilters={dashboard.filters}
            isCollapsed={hasCollapsedFilters}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};
